const MIME_BY_EXTENSION: Record<string, string> = {
  '.vcf': 'text/vcard',
  '.vcard': 'text/vcard',
  '.ics': 'text/calendar',
};

/**
 * A known extension wins over `file.type`: browsers disagree on contact cards (`text/x-vcard`,
 * or nothing at all on Windows) and the API only accepts `text/vcard`. Stricter than the old
 * asset-library behaviour, which preferred `file.type`.
 */
export const resolveFileMimeType = (file: File): string => {
  const name = file.name.toLowerCase();
  const extension = Object.keys(MIME_BY_EXTENSION).find(ext => name.endsWith(ext));

  return extension ? MIME_BY_EXTENSION[extension] : file.type;
};

/**
 * Last resort for a display/download name when the server has none on record —
 * e.g. an externally linked file (never one of our own uploads) or a legacy
 * upload from before file names were captured.
 */
export const getFileNameFromUrl = (url: string): string | undefined => {
  let segment: string;
  try {
    segment = new URL(url).pathname.split('/').pop() || '';
  } catch {
    return url.split('/').pop() || undefined;
  }

  try {
    return decodeURIComponent(segment) || undefined;
  } catch {
    // A malformed escape (e.g. a literal `%` in the key, as in presigned S3 URLs)
    // still leaves us the raw, query-string-free segment.
    return segment || undefined;
  }
};

export const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Extensions for the MIME types we accept where the subtype is *not* the extension —
 * `audio/mpeg` is an `.mp3`, `video/quicktime` a `.mov`. Values follow the IANA
 * registrations. Types whose subtype already reads as the extension (`image/png`,
 * `audio/flac`) are deliberately absent: the fallback below derives those.
 */
const EXTENSION_BY_MIME_TYPE: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/x-icon': 'ico',
  'image/vnd.microsoft.icon': 'ico',
  'image/vnd.wap.wbmp': 'wbmp',
  'image/x-portable-bitmap': 'pbm',
  'image/x-portable-pixmap': 'ppm',
  'image/x-portable-graymap': 'pgm',
  'image/x-targa': 'tga',
  'image/x-tga': 'tga',
  'image/x-xbitmap': 'xbm',
  'image/x-xpixmap': 'xpm',
  'video/quicktime': 'mov',
  'video/x-msvideo': 'avi',
  'video/x-ms-wmv': 'wmv',
  'video/x-matroska': 'mkv',
  'video/mp2t': 'ts',
  'video/vnd.dlna.mpeg-tts': 'ts',
  'video/3gpp': '3gp',
  'video/3gpp2': '3g2',
  'audio/mpeg': 'mp3',
  'audio/mp4': 'm4a',
  'audio/midi': 'mid',
  'audio/x-midi': 'mid',
  'audio/x-aiff': 'aiff',
  'audio/x-ms-wma': 'wma',
  'audio/basic': 'au',
  'audio/3gpp': '3gp',
  'audio/3gpp2': '3g2',
  'text/plain': 'txt',
  'text/markdown': 'md',
  'text/tab-separated-values': 'tsv',
  'text/vcard': 'vcf',
  'text/x-vcard': 'vcf',
  'text/directory': 'vcf',
  'text/calendar': 'ics',
  'application/icalendar': 'ics',
  'application/calendar+xml': 'xcs',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-powerpoint': 'ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
  'application/vnd.oasis.opendocument.text': 'odt',
  'application/vnd.oasis.opendocument.spreadsheet': 'ods',
  'application/vnd.oasis.opendocument.presentation': 'odp',
  'application/vnd.apple.keynote': 'key',
};

/**
 * The extension a file of this MIME type should carry, without the leading dot,
 * or `undefined` when the type says nothing usable about the file name.
 *
 * A MIME subtype is not an extension, so it can't simply be sliced off: `image/svg+xml`
 * is an `.svg`, not an `.svg+xml`. Known types come from the table above; anything else
 * is derived by dropping the structured-syntax suffix (`+xml`, `+zip`, `+json`, which
 * describe the encoding rather than the format) and the registration-tree prefix, then
 * accepting the result only if it actually looks like an extension. That last check is
 * what rejects `application/octet-stream` and `application/vnd.google-apps.calendar` —
 * types that identify no particular file — instead of inventing a nonsense suffix.
 */
export const getFileExtensionFromMimeType = (mimeType?: string): string | undefined => {
  // A MIME type may carry parameters, e.g. `image/svg+xml;charset=utf-8`.
  const type = (mimeType || '').split(';')[0].trim().toLowerCase();
  if (EXTENSION_BY_MIME_TYPE[type]) {
    return EXTENSION_BY_MIME_TYPE[type];
  }

  const subtype = type.split('/')[1] || '';
  const candidate = subtype.split('+')[0].replace(/^(x-|vnd\.|prs\.)/, '');

  return /^[a-z0-9]{2,5}$/.test(candidate) ? candidate : undefined;
};

/**
 * Splits a stored file name into the editable basename and the extension, so
 * the asset-library rename/upload fields can show `logo` while we keep `svg`
 * to reconstruct the real name on save.
 *
 * Only a suffix that looks like an extension is split off — 1–5 alphanumeric
 * characters after a last dot that is not the first character. A lone `.svg`,
 * `Summer Sale v2`, or a missing name is left unsplit.
 */
export const splitFileNameAndExtension = (filename: string): { name: string, extension?: string } => {
  if (!filename) {
    return { name: filename, extension: undefined };
  }
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex > 0) {
    const nameWithoutExt = filename.substring(0, lastDotIndex);
    const extension = filename.substring(lastDotIndex + 1);
    if (/^[a-z0-9]{1,5}$/i.test(extension))
      return { name: nameWithoutExt, extension: extension };
  }
  return { name: filename, extension: undefined };
};

/**
 * Filename suffixes that name the same format as the extension derived from
 * the MIME type. `image/jpeg` is a `jpg`, but uploads arrive as `.jpeg` too.
 */
const EXTENSION_ALIASES: Record<string, string> = {
  jpeg: 'jpg',
  jpe: 'jpg',
  jfif: 'jpg',
  tif: 'tiff',
  htm: 'html',
  mpg: 'mpeg',
  mpe: 'mpeg',
  midi: 'mid',
  aif: 'aiff',
  vcard: 'vcf',
};

const canonicalFileExtension = (extension: string) => {
  const lower = extension.toLowerCase();
  return EXTENSION_ALIASES[lower] ?? lower;
};

/**
 * Label parts for a card or list row.
 *
 * `name` is the basename when the filename suffix matches the MIME extension,
 * including known aliases (`photo.jpeg` and `image/jpeg`'s `jpg`), and also
 * when the MIME type gives no extension and the filename has one. When the
 * two extensions differ, the filename is kept whole. `extension` is the MIME
 * extension, or the filename suffix when the MIME type gives none. Comparison
 * is case insensitive. A missing name becomes `Untitled`. A lone `.svg` is
 * not treated as a complete name.
 */
export const getFileDisplayNameAndExtension = (
  filename?: string,
  mimeType?: string,
): { name: string; extension?: string } => {
  const extensionFromMimeType = getFileExtensionFromMimeType(mimeType);
  const { name, extension } = splitFileNameAndExtension(filename ?? '');
  const extensionsMatch =
    !!extension &&
    !!extensionFromMimeType &&
    canonicalFileExtension(extension) === canonicalFileExtension(extensionFromMimeType);
  const useBasename = extensionsMatch || (!extensionFromMimeType && !!extension);

  return {
    name: useBasename ? name || 'Untitled' : filename || 'Untitled',
    extension: extensionFromMimeType ?? extension,
  };
};

/**
 * Gives a download name the extension implied by its MIME type, so the OS opens it
 * with the right application.
 *
 * Built from `getFileDisplayNameAndExtension`. When the filename suffix matches
 * the MIME extension, including aliases such as `.jpeg` and `jpg`, the result is
 * the basename plus the MIME extension (`photo.jpeg` and `image/jpeg` become
 * `photo.jpg`). When they differ, the filename stays whole and the MIME
 * extension is appended (`notes.txt` and `image/png` become `notes.txt.png`).
 * A lone `.svg` is not treated as a complete name.
 */
export const ensureFileExtension = (filename: string, mimeType?: string): string => {
  const { name, extension } = getFileDisplayNameAndExtension(filename, mimeType);
  return extension ? `${name}.${extension}` : name;
};
